// The device catalogue's public API: typed, async reads over the SQLite file this device downloaded
// (opt-in, About > Backends), plus the state and actions the UI needs to manage it. Everything the
// autocomplete, scan and detail screens need from the catalogue is imported from here:
//
//   import * as catalogue from "../lib/catalogue";
//   const producers = await catalogue.searchProducers({ q: "chat mar" });
//
// Reads reject when no catalogue is installed (check isCatalogueReady() or use the lib/api.ts
// wrappers, which fall back to druif). An unknown id resolves to `undefined`.
import { withCatalogue } from "./manager";
import type {
  CatalogueAppellation,
  CatalogueClimateYear,
  CatalogueProducer,
  CatalogueRegion,
  CatalogueVintageQuality,
  CatalogueWine,
  GtinMatch,
  SearchProducersQuery,
  SearchWinesQuery,
} from "./types";

export { foldKey } from "./fold";
export {
  checkCatalogue,
  downloadCatalogue,
  ensureCatalogue,
  getCatalogueState,
  isCatalogueReady,
  removeCatalogue,
  subscribeCatalogue,
  useCatalogueState,
  withCatalogue,
  type CatalogueErrorCode,
  type CatalogueOffer,
  type CatalogueState,
  type CatalogueTask,
} from "./manager";
export type { UpdateBlocker } from "./network";
export {
  answerFirstPrompt,
  declineUpdate,
  startCatalogue,
  useCataloguePrompt,
  type CataloguePromptKind,
} from "./lifecycle";
export type { InstalledRef, InstallProgress, StoreKind } from "./protocol";
export type * from "./types";

/** A scanned barcode (any length 8 to 14, padded to GTIN-14) with its shop products and wine. */
export function lookupByGtin(gtin: string): Promise<GtinMatch> {
  return withCatalogue((c) => c.query("lookupByGtin", gtin));
}

/**
 * Wines matching `q`, best first, diacritics and typos forgiven. With `producerId` only that
 * producer's wines are searched, and an empty `q` lists them all by name.
 */
export function searchWines(query: SearchWinesQuery): Promise<CatalogueWine[]> {
  return withCatalogue((c) => c.query("searchWines", query));
}

/** Producers whose name starts with, or contains, `q`; two characters are enough. */
export function searchProducers(query: SearchProducersQuery): Promise<CatalogueProducer[]> {
  return withCatalogue((c) => c.query("searchProducers", query));
}

export function getWine(id: number): Promise<CatalogueWine | undefined> {
  return withCatalogue((c) => c.query("getWine", id));
}

export function getProducer(id: number): Promise<CatalogueProducer | undefined> {
  return withCatalogue((c) => c.query("getProducer", id));
}

export function getAppellation(id: number): Promise<CatalogueAppellation | undefined> {
  return withCatalogue((c) => c.query("getAppellation", id));
}

export function getRegion(id: number): Promise<CatalogueRegion | undefined> {
  return withCatalogue((c) => c.query("getRegion", id));
}

/** The archived growing seasons of an appellation, oldest first; empty from a catalogue before schema 3. */
export function getClimateYears(appellationId: number): Promise<CatalogueClimateYear[]> {
  return withCatalogue((c) => c.query("getClimateYears", appellationId));
}

/** The growing-season verdict for a wine's appellation in `year`, from the catalogue's own rules. */
export function getVintageQuality(
  wineId: number,
  year: number,
): Promise<CatalogueVintageQuality | undefined> {
  return withCatalogue((c) => c.query("getVintageQuality", wineId, year));
}
